import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

/**
 * Compact EN | EL toggle. Persists choice in localStorage via i18next-browser-languagedetector.
 * Also keeps `<html lang>` in sync with the active language so screen
 * readers announce content in the correct language (WCAG 3.1.1).
 */
export default function LanguageSwitcher({ className = '' }) {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);

  // Keep <html lang> aligned with the active i18n language. Runs on mount
  // and whenever the language changes.
  useEffect(() => {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = current;
    }
  }, [current]);

  const set = (lng) => {
    if (current !== lng) i18n.changeLanguage(lng);
  };

  const btn = (lng, label) => (
    <button
      key={lng}
      type="button"
      onClick={() => set(lng)}
      data-testid={`lang-${lng}`}
      aria-label={lng === 'en' ? 'Switch to English (EN)' : 'Switch to Greek (EL) — Αλλαγή σε Ελληνικά'}
      aria-pressed={current === lng}
      className={`px-2 py-1 text-xs font-semibold rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        current === lng
          ? 'bg-blue-900 text-white'
          : 'text-gray-900 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className={`inline-flex items-center gap-1 border border-gray-400 rounded-md px-1 py-0.5 bg-white ${className}`}
      title="Language / Γλώσσα"
      data-testid="language-switcher"
      role="group"
      aria-label="Language selector"
    >
      <Globe className="h-4 w-4 text-gray-800 ml-1" aria-hidden="true" />
      {btn('en', 'EN')}
      {btn('el', 'EL')}
    </div>
  );
}
